import { useEffect, useRef, useState, useCallback, memo, useMemo } from 'react'
import { isEmpty } from 'lodash'
import {
  Autocomplete,
  Skeleton,
  TextField,
  CircularProgress,
  InputAdornment,
  Checkbox,
} from '@mui/material'
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank'
import CheckBoxIcon from '@mui/icons-material/CheckBox'
import { useTranslation } from 'react-i18next'

import OptionWithTooltip, {
  shouldShowTooltip,
} from 'components/Shared/OptionWithTooltip'
import CustomChip from 'components/Shared/Chip'
import { MultiSelectWithSearchType } from 'types/MultiSelectWithSearch'
import {
  DEFAULT_CHAR_LIMIT,
  zIndexLoweredFilterCSS,
  zIndexRaisedFilterCSS,
} from 'lib/GlobalConstants'
import { useAppSelector } from 'redux/Hook'
import usePrevious from 'lib/hooks/UsePrevious'

const checkboxOutlineIcon = <CheckBoxOutlineBlankIcon fontSize="small" />
const checkedIcon = <CheckBoxIcon fontSize="small" />

// To make autocomplete input filed float over parent content
const textFieldCSS = {
  width: '15.63rem', // default width
  background: 'white',
  zIndex: 1,
  position: 'absolute',
}

const MultiSelectWithNone = (props: MultiSelectWithSearchType) => {
  const { t } = useTranslation()
  const inputRef = useRef({ focus: () => null })
  const {
    style = {},
    charLimit = DEFAULT_CHAR_LIMIT,
    icon,
    options = [],
    limitTags = 1,
    optionKey = '',
    dataTestId = '',
    labelKey = '',
    placeholderKey = '',
    defaultOpen,
    handleOnChange,
    isOptionEqualToValue,
    isLoaderEnabled = false,
    selectedValues = [],
  } = props

  const [cssUpdates, setCssUpdates] = useState<any>(zIndexLoweredFilterCSS)
  const [selectedOptions, setSelectedOptions] = useState<any>(selectedValues)
  const checkIfNoneKey = (element: any) =>
    ['None', 'Aucune', 'Aucun'].includes(element)
  const currentUser = useAppSelector((state) => state?.user?.user)
  const prevProps = usePrevious({ options, defaultOpen })
  const loader = useMemo(
    () =>
      isLoaderEnabled && typeof prevProps === 'undefined' && !options.length,
    [options, isLoaderEnabled],
  )

  // this is used to create the none object in case data passed is different than the format {i.e. name, key/type , id}
  const createNoneMenu: any = () => {
    if (isEmpty(options)) return []
    const result: any = {}

    Object.keys(options?.[0])?.map((item: any) => {
      let value
      switch (item) {
        case 'id':
          value = 0 // will keep id always as 0 since we dont have such id in db
          break
        case optionKey:
        default:
          value = t(`filters.status.none`)
          break
      }

      return (result[item] = value)
    })

    return [result]
  }

  // update only none selection value
  const updatedLang = (selection: any = []) => {
    if (!checkIfNoneKey(selection[0][optionKey])) return []
    // used to translate the none selection
    let option = selection?.[0]

    Object.keys(option)?.forEach((key) => {
      if (checkIfNoneKey(option[`${key}`])) {
        option = { ...option, [key]: t('filters.status.none') }
      }
    })
    return [option]
  }

  // Translate based on the options list for the selected options
  //update all the selection except the none selection ex. published/archived
  const updateAllSelectionLang = (data: any = []) => {
    return data.map((pState: any) => ({
      ...pState,
      ...options?.find((item: any) => {
        return item?.id && pState?.id
          ? item.id === pState.id
          : item[optionKey] === pState[optionKey]
      }),
    }))
  }

  const renderTags = (tagValue: any = [], getTagProps: any) => {
    return tagValue?.map((option: any, index: number) => (
      <CustomChip
        key={`${option[optionKey]}-${index}`}
        size="small"
        filterValues={[option[optionKey]]}
        extraProps={{
          ...getTagProps({ index }),
          color: 'primary',
        }}
      />
    ))
  }

  const renderOptions = (props: any, option: any, state: any) => {
    const customProps = { ...props, ...{ key: props.id } }
    return (
      <li {...customProps}>
        <Checkbox
          icon={checkboxOutlineIcon}
          checkedIcon={checkedIcon}
          checked={state.selected}
          sx={{ marginRight: '0.5rem' }}
        />
        {shouldShowTooltip(option[optionKey]) ? (
          <OptionWithTooltip
            option={option?.[optionKey]}
            charLimit={charLimit}
          />
        ) : (
          option[optionKey]
        )}
      </li>
    )
  }

  // Set icon if passed in props
  const getIconForTextField = (inputProps: any) => {
    return {
      ...inputProps,
      startAdornment: (
        <>
          <InputAdornment position="start">{icon}</InputAdornment>
          {inputProps.startAdornment}
        </>
      ),
    }
  }

  const renderInput = (params: any) => {
    const inputProps = icon
      ? getIconForTextField(params.InputProps)
      : params.InputProps

    const loaderInputProps = {
      ...(isLoaderEnabled && {
        endAdornment: (
          <>
            {loader ? (
              <CircularProgress
                color="inherit"
                size={20}
                sx={{ marginRight: '2rem' }}
              />
            ) : (
              <></>
            )}
            {params.InputProps.endAdornment}
          </>
        ),
      }),
    }
    const updateinputProps = { ...inputProps, ...loaderInputProps }
    const cssStyles = { ...textFieldCSS, ...cssUpdates }
    return (
      <TextField
        {...params}
        variant="outlined"
        size="small"
        inputRef={inputRef}
        style={{
          ...cssStyles,
          ...style,
        }}
        InputProps={updateinputProps}
        data-testid={dataTestId}
        label={t(labelKey)}
        placeholder={t(placeholderKey)}
        onFocus={() => {
          setCssUpdates(zIndexRaisedFilterCSS)
        }}
        onBlur={() => {
          setCssUpdates(zIndexLoweredFilterCSS)
        }}
      />
    )
  }

  // this will help to select, deselct and clear all the options
  const handleChange = useCallback(
    (event: any, selectedOptions: any, reason: any) => {
      const noneOption = createNoneMenu()

      if (['selectOption', 'removeOption'].includes(reason)) {
        if (
          selectedOptions.find((option: any) =>
            checkIfNoneKey(option[optionKey]),
          )
        ) {
          setSelectedOptions(noneOption)
          return handleOnChange(noneOption)
        } else {
          setSelectedOptions(selectedOptions)
          return handleOnChange(selectedOptions)
        }
      } else if (reason === 'clear') {
        setSelectedOptions([])
        return handleOnChange([])
      }
    },
    [selectedOptions],
  )

  // make other options disabled other than none
  const getOptionDisabled = useCallback(
    (option: any) => {
      const checkIfOptionInSelection = selectedOptions.find((element: any) =>
        checkIfNoneKey(element[optionKey]),
      )
      const filter =
        !!checkIfOptionInSelection &&
        !!options.find((element: any) => {
          return (
            !checkIfNoneKey(element[optionKey]) &&
            JSON.stringify(element) === JSON.stringify(option)
          )
        })

      return filter
    },
    [selectedOptions, options],
  )

  //used to translate the input value
  useEffect(() => {
    const newOptions = updateAllSelectionLang(selectedOptions)

    if (
      !isEmpty(selectedOptions) &&
      !!selectedOptions.find((item: any) => checkIfNoneKey(item[optionKey]))
    ) {
      const updateTranslsations = updatedLang(selectedOptions)

      setSelectedOptions(updateTranslsations)
    } else {
      setSelectedOptions(newOptions)
    }
  }, [currentUser])

  useEffect(() => {
    defaultOpen && inputRef?.current?.focus()
  }, [defaultOpen])

  useEffect(() => {
    const noneOption = createNoneMenu()

    !isEmpty(options) &&
      selectedValues?.[0]?.id === 0 &&
      setSelectedOptions(noneOption)
  }, [options, selectedValues])

  const autoCompleteOptions: any = {
    loading: loader,
    loadingText: <Skeleton height={20} />,
    multiple: true,
    id: labelKey,
    size: 'small',
    openOnFocus: true,
    style,
    disableCloseOnSelect: true,
    value: selectedOptions,
    limitTags: limitTags,
    options: !isEmpty(options) ? [...createNoneMenu(), ...options] : [],
    onChange: (event: any, value: any, reason: any) =>
      handleChange(event, value, reason),
    getOptionLabel: (option: any) => (optionKey && option?.[optionKey]) || '',
    isOptionEqualToValue,
    renderTags,
    renderOption: (props: any, option: any, state: any) =>
      options.length ? renderOptions(props, option, state) : null,
    renderInput,
    getOptionDisabled,
    openText: t('filters.ariaTexts.open'),
    closeText: t('filters.ariaTexts.close'),
    clearText: t('filters.ariaTexts.clear'),
    noOptionsText: t('filters.ariaTexts.noOptions'),
  }

  return <Autocomplete {...autoCompleteOptions} />
}

export default memo(MultiSelectWithNone)
